import { createCollection, localStorageCollectionOptions } from "@tanstack/react-db";
import type { Page } from "../page";

/** `startSync: true` because non-React readers touch this collection
 * outside any subscription — the upload queue's stuck-page reclaim and
 * `deleteScriptAndPages` both read `toArray` directly. Sync is otherwise
 * lazy (it starts on the first subscriber, i.e. the first component
 * render), so those readers saw an *empty* collection and silently did
 * nothing — which is how pages left mid-upload stayed "uploading" across
 * a reload forever. Hydrating from localStorage is synchronous and cheap. */
export const pagesCollection = createCollection(
  localStorageCollectionOptions<Page>({
    storageKey: "camera-scanner-pages",
    getKey: page => page.id,
    startSync: true,
  }),
);

// Keyed per row, plus a collection-wide fallback: a retake (delete old row,
// insert new row under a fresh id) pairs a removal with an insertion of a
// *different* key, so waiting on the row's own key alone isn't enough to
// separate it from the next write — see settleRow's doc comment.
const lastMutation = new Map<string, Promise<unknown>>();
let lastAny: Promise<unknown> = Promise.resolve();

/** @tanstack/db 0.8.7 throws "Query contributors with the same row key
 * are not congruent" — and drops the write — when a write to this
 * collection lands before a recent prior write has fully settled through
 * the live-query engine (TanStack/db#1767, #1783, unfixed as of the
 * latest published release). Callers that fire several quick writes at
 * this collection (capture → hash patch → upload-queue claim, a retake's
 * delete-then-insert, etc.) must track each mutation's transaction here
 * and await `settleRow` before the next one. */
export function trackPageMutation(id: string, tx: { isPersisted: { promise: Promise<unknown> } }): void {
  const settled = tx.isPersisted.promise.catch(() => undefined);
  lastMutation.set(id, settled);
  lastAny = settled;
}

export async function settleRow(id: string): Promise<void> {
  await Promise.all([lastMutation.get(id), lastAny]);
}
